import React, { useEffect, useMemo, useRef, useState } from 'react';

import { createAdmission } from '../../services/api/admissionApi';
import { searchPatientByMobile, getPatient, mapDoctorsResponse } from '../../services/api/visitApi';
import { lookupPatientByABHA, claimDiscoveredScan } from '../../services/api/abhaApi';
import { snapshotLookupMobile, formatPatientName } from '../../services/api/abhaProfile';
import { claimErrorMessage, EXISTING_PATIENT_VERIFY_NOTICE } from '../../utils/scanShare';
import { takeTransientAadhaar } from '../../utils/transientAadhaar';
import { DIGITAL_RECORDS_KEY } from '../../services/api/consentApi';
import useDepartmentStore from '../../stores/departmentStore';
import useOrgSettingsStore from '../../stores/orgSettingsStore';
import { SPECIALIZATION_LABELS, STAFF_ROLES, ASSIGNABLE_PURPOSE } from '../../constants/staff';
import { useScope, usePermission } from '../../context/PermissionContext';
import useAssignableProviders, {
  DOCTORS_LOADING_COPY,
  DOCTORS_LOAD_ERROR_COPY,
  doctorsEmptyCopy,
  DOCTORS_SCOPED_FOOTER_COPY,
} from '../../hooks/useAssignableProviders';
import {
  resolveDepartmentPicker,
  filterProvidersByDept,
  rederiveDepartmentOnProviderChange,
} from '../visit/departmentResolution';
import {
  ADMISSION_SOURCE,
  ADMISSION_SOURCES,
  PAYER_TYPES,
  PAYER_TYPE_DEFAULT,
  isEmergencySource,
  isElectiveSource,
  ADMISSION_ACCESS_ERROR_CODE,
  ADMISSION_ACCESS_ERROR_COPY,
  DEPT_ACCESS_BLOCKED_COPY,
  duplicateOutOfScopeLines,
} from '../../constants/admission';

import ConfirmationModal from '../common/ConfirmationModal';
import ConsentBlock, { resolveConsentGranted } from '../common/ConsentBlock';
import AttenderFields from './AttenderFields';
import EmergencyMlcSection from './EmergencyMlcSection';
import WardBedPicker from './WardBedPicker';
import PlannedInterventionsPicker from './PlannedInterventionsPicker';
import EstimatePanel from './EstimatePanel';
import InlineRegisterPatientDialog from './InlineRegisterPatientDialog';
import AbhaBadge from '../visit/AbhaBadge';
import useAbdmEnabled from '../../hooks/useAbdmEnabled';
import { abhaStatusMeta } from '../visit/abhaStatusMeta';
import AbhaVerifyModal from '../abha/AbhaVerifyModal';
import AbhaLinkConfirmDialog from '../abha/AbhaLinkConfirmDialog';
import AdmittedElsewhereLabel from '../common/AdmittedElsewhereLabel';
import { SCAN_SHARE_COPY } from '../../constants/abdmCopy';
import { patientAgeDisplay } from '../../utils/ageValue';

/** Stable idempotency key per form session (client UUID, reused across retries). */
function newIdempotencyKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `adm-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Format a Date into the value a `<input type="datetime-local">` expects
 * ("YYYY-MM-DDTHH:mm", local time, no timezone suffix).
 */
function toDatetimeLocalValue(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const EMPTY_MLC = { is_mlc: false, mlc_number: '', police_station: '', police_informed_at: '' };

/**
 * AdmitPatientForm (S1/S4 — Variant A single-page). Patient lookup → admission
 * details (doctor + auto-derived dept + diagnosis + payer + attender) → planned
 * interventions (elective only) → ward/bed picker → estimate rail → submit.
 *
 * Dept auto-derives from the PRIMARY doctor (A2 pattern, ONE source =
 * departmentResolution). Emergency relaxes bed/doctor/diagnosis, reveals MLC,
 * hides planned interventions. Idempotency key generated ONCE and reused on
 * retry. 409 BED_OCCUPIED → form preserved + re-pick prompt. 409
 * DUPLICATE_ACTIVE_ADMISSION → link to the existing admission, or (walled
 * receptionist, `in_scope:false`) the IP no. + department with no link.
 *
 * admission-access S10: the primary-doctor list is the server-scoped
 * assignable list; the department dropdown offers the viewer's departments.
 * R2 (owner 2026-10-06): an EMERGENCY admission is org-wide — for a walled viewer
 * the doctor list comes from `?purpose=emergency` and the department dropdown
 * offers every department (the server derives the department from the doctor).
 */
export default function AdmitPatientForm({ onAdmitted, onViewAdmission, initialAbhaSnapshot = null }) {
  // ── Reference data (departments + doctor→dept edges) ───────────────────
  // The primary-doctor PICKER list is served scoped by the server
  // (GET /providers/assignable?role=DOCTOR — one request per session, shared with
  // every other picker); names resolve via mapDoctorsResponse, never raw UUIDs.
  // ChangeDoctorDialog / Add-doctor stay org-wide on useDoctorRoster.
  const departments = useDepartmentStore((s) => s.departments);
  const deptsByMember = useDepartmentStore((s) => s.deptsByMember);
  const hydrateAllMemberDepts = useDepartmentStore((s) => s.hydrateAllMemberDepts);
  // Doctor→dept edges (dept auto-fill + dept-first filter). The store action is
  // single-flight + skip-once-done and its reference is stable.
  useEffect(() => { hydrateAllMemberDepts(); }, [hydrateAllMemberDepts]);

  const [source, setSource] = useState(ADMISSION_SOURCE.PLANNED);
  const emergency = isEmergencySource(source);
  const elective = isElectiveSource(source);

  // The wall applies to a department-scoped viewer (a non-admin with >=1
  // department) on a PLANNED admission only: EMERGENCY (casualty) is org-wide (R2).
  const { isAdmin: viewerIsAdmin, viewerDeptIds } = useScope();
  const viewerIsDeptScoped = !viewerIsAdmin && Array.isArray(viewerDeptIds) && viewerDeptIds.length > 0;
  const pickerIsWalled = viewerIsDeptScoped && !emergency;
  // Only a walled viewer asks for the org-wide list: everyone else already gets it
  // from the default call (no extra request). `undefined` keeps the default call.
  const { providers: assignableDoctors, loading: doctorsLoading, error: doctorsError } =
    useAssignableProviders(
      STAFF_ROLES.DOCTOR,
      viewerIsDeptScoped && emergency ? ASSIGNABLE_PURPOSE.EMERGENCY : undefined,
    );
  const doctors = useMemo(() => mapDoctorsResponse(assignableDoctors), [assignableDoctors]);

  // Department dropdown = the viewer's own departments while the wall applies.
  // Admin / unassigned viewers (and any EMERGENCY admission) keep the org list.
  // Display-only narrowing of the options: the doctor list above is narrowed by
  // the server. Fed to resolveDepartmentPicker below, which also intersects a
  // multi-department doctor's departments with it.
  const pickerDepartments = useMemo(
    () => (pickerIsWalled
      ? (departments || []).filter((d) => viewerDeptIds.includes(d.dept_id || d.id))
      : departments),
    [departments, pickerIsWalled, viewerDeptIds],
  );

  // ── Patient lookup ─────────────────────────────────────────────────────
  const [mobile, setMobile] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [patient, setPatient] = useState(null);
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  // R43: link an ABHA to the EXISTING selected patient (verify -> "same person?" confirm).
  const { show: abdmOn } = useAbdmEnabled();
  const canScanShare = usePermission('ABHA_SCAN_SHARE') && abdmOn; // R131: without it skip the claim -> OTP verify path
  const [abhaVerifying, setAbhaVerifying] = useState(false);
  const [abhaSnapshot, setAbhaSnapshot] = useState(null);   // set => AbhaLinkConfirmDialog open
  const [registerAbhaSnapshot, setRegisterAbhaSnapshot] = useState(null);  // "different person" -> new patient
  const [abhaNote, setAbhaNote] = useState('');

  // IP-3 (R44): arrived from the ABHA page with a verified snapshot. ONCE on mount: an existing
  // patient already linked to this ABHA -> select; else a patient on the same mobile -> select and
  // open the link-confirm; several on that mobile -> list them; none -> register dialog prefilled.
  // The ref guard makes it one run (StrictMode/re-render safe); Aadhaar is the one-shot in-memory hand-off.
  const handoffDone = useRef(false);
  useEffect(() => {
    if (!initialAbhaSnapshot || handoffDone.current) return;
    handoffDone.current = true;
    const snapshot = { ...initialAbhaSnapshot, aadhaar: takeTransientAadhaar() };
    const digits = snapshotLookupMobile(snapshot);
    if (snapshot.scanId) { startScan(snapshot, digits); return; }   // R56: picked from the Scan & Share panel
    (async () => {
      try {
        const found = snapshot.abhaNumber ? await lookupPatientByABHA({ abhaNumber: snapshot.abhaNumber }) : null;
        if (found?.found && found.patientId) {
          let existing = null;
          try { existing = await getPatient(found.patientId); } catch (_) { /* name below */ }
          setPatient(existing || { patient_id: found.patientId, name: found.patientName });
          return;
        }
      } catch (_) { /* fall through to the mobile search */ }
      if (digits.length === 10) {
        setMobile(digits);
        let matches = [];
        try { matches = (await searchPatientByMobile(digits)) || []; } catch (_) { /* treat as none */ }
        if (matches.length === 1) { setPatient(matches[0]); setAbhaSnapshot(snapshot); return; }
        if (matches.length > 1) { setSearchResults(matches); return; }
      }
      setRegisterAbhaSnapshot(snapshot);
      setShowRegisterDialog(true);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAbhaSnapshot]);

  // R56: a scan picked in the desk-wide Scan & Share panel arrives as initialAbhaSnapshot (already
  // revealed; carries scanId). Existing patient on that mobile -> select + confirm -> server claim
  // (claim->link->consume); none -> register dialog prefilled, claim right after create. Claim
  // failures are shown, never swallowed.
  const [scanClaim, setScanClaim] = useState(null);   // { scanId } while a picked scan awaits its patient
  const [scanMsg, setScanMsg] = useState('');
  // A picked scan whose mobile belongs to an EXISTING patient is never claimed (the server refuses it):
  // staff are told up front and the normal OTP verify opens, prefilled with the mobile.
  // R78: an already-VERIFIED patient first tries the claim (server attaches it with no OTP when the
  // scanned ABHA equals the verified one); only a 409 SCAN_SHARE_EXISTING_PATIENT falls back to verify.
  const [scanVerifyFallback, setScanVerifyFallback] = useState(false);
  const scanTriedRef = useRef('');
  const scanExisting = Boolean(scanClaim && patient && !showRegisterDialog
    && (patient.abha_status !== 'VERIFIED' || scanVerifyFallback || !canScanShare));
  useEffect(() => {
    if (!scanClaim || !patient || showRegisterDialog || patient.abha_status !== 'VERIFIED' || scanVerifyFallback || !canScanShare) return;
    const id = patient.patient_id || patient.id;
    const key = `${scanClaim.scanId}:${id}`;
    if (scanTriedRef.current === key) return;
    scanTriedRef.current = key;
    claimDiscoveredScan({ patientId: id, scanId: scanClaim.scanId })
      .then((r) => { setScanClaim(null); setScanMsg(''); setAbhaNote(r.sameAbha ? "Linked to this patient's existing ABHA." : 'ABHA linked from Scan & Share.'); })
      .catch((e) => {
        if (e?.abhaCode === 'SCAN_SHARE_EXISTING_PATIENT') { setScanVerifyFallback(true); return; }
        setScanClaim(null);
        setScanMsg(SCAN_SHARE_COPY.claimFailed(claimErrorMessage(e)));
      });
  }, [scanClaim, patient, showRegisterDialog, scanVerifyFallback, canScanShare]);
  const runClaim = async (p, scanId) => {
    const id = p.patient_id || p.id;
    try {
      await claimDiscoveredScan({ patientId: id, scanId });
      setPatient({ ...p, abha_status: 'VERIFIED' });
      setScanMsg('');
      setAbhaNote('ABHA linked from Scan & Share.');
    } catch (e) {
      setPatient(p);
      setScanMsg(SCAN_SHARE_COPY.claimFailed(claimErrorMessage(e)));
    }
  };
  async function startScan(snapshot, digits) {
    setScanMsg('');
    setScanVerifyFallback(false);
    scanTriedRef.current = '';
    setScanClaim({ scanId: snapshot.scanId });
    setMobile(digits);
    let matches = [];
    try { matches = (await searchPatientByMobile(digits)) || []; } catch (_) { /* treat as none */ }
    if (matches.length === 1) { setPatient(matches[0]); return; }
    if (matches.length > 1) { setSearchResults(matches); return; }
    setRegisterAbhaSnapshot(snapshot);
    setShowRegisterDialog(true);
  }

  // Auto-fire the lookup when a full 10-digit mobile is entered (debounced),
  // mirroring RegisterVisitForm — no explicit "Search" button. Bounded: a single
  // debounced call per completed number; typing resets the pending timer.
  const searchDebounceRef = useRef(null);
  // Guards against a STALE search response overwriting a newer one. Type
  // number A then change to B: if A's request resolves last, A's patients are
  // shown under B's number, and picking one loads the WRONG patient's record —
  // consent, name, allergies and all. Owner: "we should not have fields
  // flowing over to another patients". The debounce narrows the window but
  // does not close it; only comparing against the latest query does.
  const searchSeqRef = useRef(0);
  const runSearch = async (digits) => {
    const seq = ++searchSeqRef.current;
    setSearching(true);
    try {
      const results = await searchPatientByMobile(digits);
      if (seq !== searchSeqRef.current) return;   // a newer search superseded us
      setSearchResults(results || []);
    } catch (e) {
      if (seq !== searchSeqRef.current) return;
      setSearchResults([]);
    }
    if (seq !== searchSeqRef.current) return;
    setSearching(false);
  };
  const onMobileChange = (raw) => {
    const cleaned = raw.replace(/\D/g, '').slice(0, 10);
    setMobile(cleaned);
    setSearchResults(null);
    setScanClaim(null);   // retyped mobile = a different patient; the scan stays in the waiting list
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (cleaned.length === 10) {
      searchDebounceRef.current = setTimeout(() => runSearch(cleaned), 500);
    }
  };
  useEffect(() => () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
  }, []);

  // ── CONSENT-CAPTURE P1 (D6/D13) ──────────────────────────────────────
  // Catalogue fetch mirrors RegisterVisitForm's (same store, single-flight
  // cached — safe to call from both mounts, only ever one real GET).
  const consentCatalog = useOrgSettingsStore((s) => s.consentCatalog);
  const fetchConsentCatalog = useOrgSettingsStore((s) => s.fetchConsentCatalog);
  useEffect(() => {
    fetchConsentCatalog?.();
  }, [fetchConsentCatalog]);
  const consentCatalogEntry = (consentCatalog?.consents || []).find((c) => c.key === DIGITAL_RECORDS_KEY) || null;
  // Single source of truth (CLAUDE.md rule) for the derived checkbox state —
  // existing patient record wins, else the catalogue default. Re-derives on
  // every patient change (select / clear / inline-register) and once the
  // catalogue resolves.
  const digitalRecordsEntry = patient?.consents?.[DIGITAL_RECORDS_KEY] || null;
  const [consentGranted, setConsentGranted] = useState(true);
  useEffect(() => {
    setConsentGranted(resolveConsentGranted(digitalRecordsEntry, consentCatalogEntry));
  }, [digitalRecordsEntry, consentCatalogEntry]);

  // ── Admission fields ───────────────────────────────────────────────────
  const [primaryDoctorId, setPrimaryDoctorId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [wasManualDeptPick, setWasManualDeptPick] = useState(false);
  const [diagnosis, setDiagnosis] = useState('');
  const [payerType, setPayerType] = useState(PAYER_TYPE_DEFAULT);
  const [attender, setAttender] = useState({ name: '', relation: '', mobile: '' });
  const [mlc, setMlc] = useState(EMPTY_MLC);
  const [selectedBed, setSelectedBed] = useState(null);
  const [assignLater, setAssignLater] = useState(false); // "Assign bed later" (elective)
  const [confirmClearBed, setConfirmClearBed] = useState(false); // in-app confirm before dropping a picked bed
  const [expectedBedCategoryId, setExpectedBedCategoryId] = useState(''); // planned category, no-bed estimate
  const [plannedInterventions, setPlannedInterventions] = useState([]);
  const [expectedStayDays, setExpectedStayDays] = useState(1);
  const [otherLines, setOtherLines] = useState([]);

  // Optional backdated admission date/time — sliding [now-24h, now] window,
  // bounds fixed at mount (the form session is short-lived, no need to keep
  // re-computing "now" on every render). Empty by default = server defaults
  // to now, byte-identical to the pre-existing behaviour.
  const [admittedAt, setAdmittedAt] = useState('');
  const admittedAtBounds = useRef(null);
  if (!admittedAtBounds.current) {
    const now = new Date();
    admittedAtBounds.current = {
      max: toDatetimeLocalValue(now),
      min: toDatetimeLocalValue(new Date(now.getTime() - 24 * 60 * 60 * 1000)),
    };
  }

  // ── Submit state ───────────────────────────────────────────────────────
  const idempotencyKey = useRef(newIdempotencyKey());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [bedConflict, setBedConflict] = useState(false);
  const [duplicate, setDuplicate] = useState(null); // {admission_id, ip_number, status, in_scope, department_name}

  // ── Dept auto-derive (A2 pattern, single source = departmentResolution) ──
  // NOTE: unlike the visit flow, an EMERGENCY ADMISSION does NOT suppress the
  // department picker — the backend admission's `_derive_department` has no
  // emergency branch, so department stays a normal optional field regardless
  // of source. `visitType` is always 'REGULAR' here; the shared visit resolver
  // (departmentResolution.js) is untouched — visits still lock to no-department
  // on EMERGENCY.
  const activeProviderDepts = primaryDoctorId ? (deptsByMember[primaryDoctorId] || null) : null;
  const resolution = useMemo(
    () => resolveDepartmentPicker({
      departments: pickerDepartments,
      activeProviderDepts,
      hasActiveProvider: !!primaryDoctorId,
      visitType: 'REGULAR',
    }),
    [pickerDepartments, activeProviderDepts, primaryDoctorId],
  );

  // Reverse filter: dept picked first → constrain doctor list (∪ no-dept members).
  const doctorOptions = useMemo(
    () => filterProvidersByDept(doctors, departmentId, deptsByMember),
    [doctors, departmentId, deptsByMember],
  );

  const onDoctorChange = (nextDoctorId) => {
    setPrimaryDoctorId(nextDoctorId);
    const nextDepts = nextDoctorId ? (deptsByMember[nextDoctorId] || null) : null;
    const nextResolution = resolveDepartmentPicker({
      departments: pickerDepartments,
      activeProviderDepts: nextDepts,
      hasActiveProvider: !!nextDoctorId,
      visitType: 'REGULAR',
    });
    const nextDept = rederiveDepartmentOnProviderChange({
      resolution: nextResolution,
      currentDeptId: departmentId,
      providerCleared: !nextDoctorId,
      wasManualPick: wasManualDeptPick,
    });
    setDepartmentId(nextDept);
    setWasManualDeptPick(false);
  };

  const onDeptChange = (nextDeptId) => {
    setDepartmentId(nextDeptId);
    // Manual pick only when no provider is driving the value.
    setWasManualDeptPick(!primaryDoctorId);
  };

  // Emergency clears MLC on flip-away; elective re-requires bed. Manage on toggle.
  const onSourceChange = (nextSource) => {
    setSource(nextSource);
    if (!isEmergencySource(nextSource)) {
      setMlc(EMPTY_MLC); // MLC only valid on emergency (server: MLC_NOT_EMERGENCY)
    } else {
      setAssignLater(false); // toggle hidden on emergency (bed already optional) — reset OFF
    }
  };

  // "Assign bed later" toggle (elective). ON → clear + disable the pickers; if a
  // bed was already picked, open the in-app ConfirmationModal before dropping it
  // (no native window.confirm). Picking a bed elsewhere auto-clears the toggle
  // (see onSelectBed).
  const onToggleAssignLater = (next) => {
    if (next && selectedBed) {
      setConfirmClearBed(true); // confirm → onConfirmClearBed; cancel → toggle stays OFF, bed intact
      return;
    }
    setAssignLater(next);
    if (next) setSelectedBed(null);
  };

  const onConfirmClearBed = () => {
    setConfirmClearBed(false);
    setSelectedBed(null);
    setAssignLater(true);
  };

  // Bed picked → clear the toggle (a real bed always wins over "assign later").
  const onSelectBed = (bed) => {
    setSelectedBed(bed);
    if (bed) setAssignLater(false);
  };

  // Leaving EMERGENCY puts the wall back (the scoped list is refetched by the hook).
  // A doctor picked from the org-wide list may not be in it: once the scoped list has
  // settled, drop a pick it does not contain (and the department that followed it) so
  // the select never looks empty while the hidden pick still submits (server: 403).
  useEffect(() => {
    if (!pickerIsWalled || doctorsLoading || doctorsError || !primaryDoctorId) return;
    if (doctors.some((d) => d.member_id === primaryDoctorId)) return;
    setPrimaryDoctorId('');
    setDepartmentId('');
    setWasManualDeptPick(false);
  }, [pickerIsWalled, doctorsLoading, doctorsError, doctors, primaryDoctorId]);

  // Auto-fill a locked single-dept doctor's department.
  useEffect(() => {
    if (resolution.locked && resolution.autoFillDeptId && departmentId !== resolution.autoFillDeptId) {
      setDepartmentId(resolution.autoFillDeptId);
      setWasManualDeptPick(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolution.locked, resolution.autoFillDeptId]);

  // ── Estimate value (bed category from selected bed) ────────────────────
  const estimateValue = {
    expectedStayDays,
    bedCategory: selectedBed ? selectedBed.bedCategory : null,
    plannedInterventions,
    otherLines,
  };

  const removeIntervention = (index) =>
    setPlannedInterventions((list) => list.filter((_, i) => i !== index));

  // ── Validation (SINGLE SOURCE: the unmet-requirements list drives BOTH the
  //    inline readiness checklist §5 and canSubmit — never re-derived inline) ──
  const diagnosisRequired = elective;
  const bedRequired = elective;
  const needPatient = !patient;
  const needDiagnosis = diagnosisRequired && diagnosis.trim().length === 0;
  // "Assign bed later" drops the bed requirement (single source: this feeds BOTH
  // the readiness checklist and canSubmit — never re-derived inline).
  const needBed = bedRequired && !assignLater && !selectedBed;
  // Owner (live click-through) — the consent reason must surface INSIDE this
  // same checklist ("On IPD the error shuld be part of yellow box where all
  // the errors comes"), not as a second grey line under the button. Gated on
  // `!!patient` — with no patient selected, ConsentBlock shows its own
  // disabled/informational variant (no checkbox to tick yet), and "Select a
  // patient" already covers it.
  const needConsent = !!patient && (!consentGranted || !consentCatalogEntry);
  const unmetItems = useMemo(() => {
    const items = [];
    if (needPatient) items.push('Select a patient');
    if (needDiagnosis) items.push('Add a provisional diagnosis');
    if (needBed) items.push('Pick a bed');
    if (needConsent) items.push(!consentCatalogEntry ? 'Consent text unavailable — retry' : 'Read the consent to the patient and tick it');
    return items;
  }, [needPatient, needDiagnosis, needBed, needConsent, consentCatalogEntry]);
  // D13/D15.1 — GATING stays independent of the checklist array above (a
  // future bug in unmetItems must never let an unconsented admit through),
  // even though the reason is now ALSO surfaced there for display. Display
  // and gating are separate concerns — do not collapse this back to
  // `unmetItems.length === 0` alone.
  // PO ruling (validation timing): missing required fields NO LONGER disable
  // Admit. CONSENT is the only disabler, so the button's disabled state means
  // exactly one thing and its reason never competes with other complaints.
  // Without this there is nothing to "attempt", so red could never be earned.
  // D13 is fully intact — and because D6 pre-ticks, a pristine form is never
  // disabled: it can only follow a deliberate untick, which is the user's own
  // action rather than a scold on arrival.
  const consentBlocked = !consentGranted || !consentCatalogEntry;
  const canSubmit = !submitting && !consentBlocked;
  // Stage 2 begins at the first real attempt and persists for the session.
  const [attempted, setAttempted] = useState(false);
  const showFieldErrors = attempted;
  // The CHECKLIST reddens on an attempt OR the moment consent is unticked.
  // PO's own reasoning: an untick is the user's deliberate action, so it earns
  // red immediately — unlike an empty field on a pristine form. Keeping the
  // consent reason in the checklist honours the owner's "one place, not two";
  // the PO had proposed moving it beside the button, which would reinstate the
  // split he asked me to remove. Field red still needs a real attempt, so an
  // untick does not repaint the whole form.
  const showChecklistError = attempted || consentBlocked;

  const submit = async () => {
    setError(null);
    setBedConflict(false);
    setDuplicate(null);
    if (!canSubmit) return;           // consent-blocked: its own reason line says why
    if (unmetItems.length > 0) {
      // Stage 2: the attempt is what earns the red, and we take the operator
      // to the first thing that stopped them rather than leaving them to hunt.
      setAttempted(true);
      const firstId = needPatient ? 'card-patient'
        : needDiagnosis ? 'diagnosis-input'
          : needBed ? 'bed-picker-wrap' : null;
      if (firstId) {
        const el = document.querySelector(`[data-testid="${firstId}"]`);
        if (el) { el.scrollIntoView({ block: 'center' }); if (el.focus) el.focus(); }
      }
      return;
    }
    setSubmitting(true);
    const body = {
      patient_id: patient.patient_id || patient.id,
      source,
      primary_doctor_member_id: primaryDoctorId || null,
      department_id: departmentId || null,
      idempotency_key: idempotencyKey.current, // ALWAYS sent, stable across retries
      // Optional backdated admission time — omitted entirely when the user
      // never touched the picker (server defaults to now).
      ...(admittedAt ? { admitted_at: new Date(admittedAt).toISOString() } : {}),
      bed_id: selectedBed ? selectedBed.bed_id : null,
      // Explicit "assign a bed later" intent — a forgotten bed must NOT silently
      // pass (server: planned + no bed is valid ONLY when this is true). Emergency
      // ignores it (bed already optional), and the toggle resets OFF on emergency.
      admit_without_bed: elective && assignLater,
      // Planned bed category for the no-bed estimate (ignored server-side when a
      // real bed is set / not assign-later).
      expected_bed_category_id:
        (elective && assignLater && !selectedBed && expectedBedCategoryId) || null,
      provisional_diagnosis: diagnosis.trim() || null,
      payer_type: payerType,
      attender: (attender.name || attender.relation || attender.mobile)
        ? { name: attender.name || null, relation: attender.relation || null, mobile: attender.mobile || null }
        : null,
      is_mlc: emergency ? !!mlc.is_mlc : false,
      mlc_number: emergency && mlc.is_mlc ? (mlc.mlc_number || null) : null,
      police_station: emergency && mlc.is_mlc ? (mlc.police_station || null) : null,
      police_informed_at: emergency && mlc.is_mlc ? (mlc.police_informed_at || null) : null,
      expected_stay_days: expectedStayDays ?? null,
      other_estimate_lines: otherLines
        .filter((l) => l.label && Number(l.amount) >= 0)
        .map((l) => ({ label: l.label, amount: Number(l.amount) })),
      // Server snapshots names/prices/gst — client sends refs only (never prices).
      planned_interventions: elective
        ? plannedInterventions.map((l) => ({ type: l.type, catalog_ref: l.catalog_ref }))
        : null,
      // CONSENT-CAPTURE P1 (D13) — `## API CONTRACT`: ONLY the consent
      // value; the staff read-aloud instruction never leaves ConsentBlock.
      consents: { digital_records: !!consentGranted },
    };

    const res = await createAdmission(body);
    setSubmitting(false);

    if (res.success) {
      if (onAdmitted) onAdmitted(res.data);
      return;
    }
    // 409 bed race — keep the whole form, drop the bed, prompt a re-pick.
    if (res.code === 'BED_OCCUPIED') {
      setSelectedBed(null);
      setBedConflict(true);
      return;
    }
    // 409 duplicate active admission — hard block + link to the existing one
    // (no link when the walled desk cannot open it: `in_scope === false`).
    if (res.code === ADMISSION_ACCESS_ERROR_CODE.DUPLICATE_ACTIVE_ADMISSION) {
      setDuplicate({
        admission_id: res.admission_id,
        ip_number: res.ip_number,
        status: res.status,
        in_scope: res.in_scope,
        department_name: res.department_name,
      });
      return;
    }
    // 403 doctor not in the walled receptionist's departments (stale form after a
    // reassignment) — surface copy keyed by code, never the server message.
    if (res.code === ADMISSION_ACCESS_ERROR_CODE.PROVIDER_OUT_OF_SCOPE) {
      setError(ADMISSION_ACCESS_ERROR_COPY[ADMISSION_ACCESS_ERROR_CODE.PROVIDER_OUT_OF_SCOPE]);
      return;
    }
    // Owner rule 2026-10-13 — every department of this member is deactivated:
    // the server refuses the create too (403 OUT_OF_DEPARTMENT_SCOPE).
    if (res.code === ADMISSION_ACCESS_ERROR_CODE.OUT_OF_DEPARTMENT_SCOPE) {
      setError(DEPT_ACCESS_BLOCKED_COPY);
      return;
    }
    setError(res.error || 'Could not admit the patient. Please try again.');
  };

  // ── Render ─────────────────────────────────────────────────────────────
  const patientLabel = patient ? (patient.name || formatPatientName(patient)) : '';
  const patientAge = patient ? patientAgeDisplay(patient) : ''; // OR-127: DOB wins over stored age
  const cardCls = 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-sm mb-4';
  // PO: section-card outlines are DROPPED — field red + checklist red is
  // enough, and the third layer is what produced the wall of red.
  const cardFor = () => cardCls;
  const cardH = 'px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2';
  const cardB = 'p-4';
  const labelCls = 'block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1';
  const inputCls = 'w-full px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500';
  // FIELD-level error. The card outline says which SECTION; this says which
  // CONTROL. Without it an empty required field still renders a teal focus
  // ring, which reads as "valid" — the owner spotted exactly that.
  // Deliberately keeps the red ring on focus instead of falling back to teal,
  // so focusing a bad field does not make it look fixed.
  const inputErrCls = 'w-full px-2.5 py-2 rounded-lg border-2 border-red-400 dark:border-red-600 bg-red-50 dark:bg-red-900/20 text-slate-800 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500';
  const fieldFor = (unmet) => (unmet && showFieldErrors ? inputErrCls : inputCls);
  // Step-number chip: amber while its requirement is unmet (§5.2 field-level echo),
  // teal once satisfied. Same unmet booleans that drive canSubmit — single source.
  const stepChip = (n, unmet) =>
    `w-5 h-5 rounded-full text-white text-[11px] font-semibold flex items-center justify-center ${unmet ? 'bg-amber-500' : 'bg-teal-600'}`;

  return (
    <div className="max-w-5xl mx-auto p-4" data-testid="admit-patient-form">
      <h1 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Admit Patient</h1>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-4 items-start">
        <div>
          {/* 1. PATIENT */}
          <div className={cardFor(needPatient)} data-testid="card-patient">
            <div className={cardH}>
              <span className={stepChip(1, needPatient)}>1</span>
              <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Patient</h2>
            </div>
            <div className={cardB}>
              {patient ? (
                <div className="flex items-center gap-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-3" data-testid="patient-selected">
                  <div className="flex-1">
                    <div className="font-semibold text-slate-800 dark:text-slate-100">
                      {patientLabel || 'Existing patient'}
                    </div>
                    {!patientLabel && (
                      <div className="text-[12px] text-amber-700 dark:text-amber-400">Patient details could not be loaded.</div>
                    )}
                    <div className="text-[12px] text-slate-500 dark:text-slate-400">
                      {[patient.mobile || mobile, patientAge ? `Age ${patientAge}` : '', patient.gender || '']
                        .filter(Boolean).join(' · ')}
                    </div>
                    {abdmOn && (
                      <div className="mt-1 flex items-center gap-2" data-testid="patient-selected-abha">
                        <AbhaBadge status={patient.abha_status} isDark={document.documentElement.classList.contains('dark')} />
                      </div>
                    )}
                    {scanMsg && <div className="mt-1 text-[12px] text-red-700 dark:text-red-300" data-testid="scan-share-error">{scanMsg}</div>}
                    {abhaNote && <div className="mt-1 text-[12px] text-amber-700 dark:text-amber-300" data-testid="abha-note">{abhaNote}</div>}
                  </div>
                  <button
                    type="button"
                    className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm"
                    onClick={() => { setPatient(null); setSearchResults(null); setAbhaNote(''); setScanClaim(null); setScanMsg(''); }}
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div>
                  <div className="relative">
                    <input
                      className={inputCls}
                      placeholder="Enter 10-digit mobile number"
                      inputMode="tel"
                      value={mobile}
                      onChange={(e) => onMobileChange(e.target.value)}
                      aria-label="Patient mobile"
                    />
                    {searching && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-slate-500 dark:text-slate-400">
                        Searching…
                      </span>
                    )}
                  </div>
                  {scanMsg && <p className="mt-2 text-[12px] text-red-700 dark:text-red-300" data-testid="scan-share-error">{scanMsg}</p>}
                  {searchResults && searchResults.length === 0 && (
                    <div className="mt-2" data-testid="no-match">
                      <p className="text-[12.5px] text-slate-500 dark:text-slate-400 mb-2">
                        No patient found for this mobile number.
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowRegisterDialog(true)}
                        className="px-3 py-1.5 rounded-lg border border-teal-500 bg-teal-50 dark:bg-teal-900/20 text-teal-800 dark:text-teal-200 text-sm font-medium"
                        data-testid="open-register-patient"
                      >
                        + Register new patient
                      </button>
                    </div>
                  )}
                  {searchResults && searchResults.length > 0 && (
                    <div className="mt-2" data-testid="patient-results">
                      {searchResults.map((p) => {
                        const pName = (p.name || formatPatientName(p)) || 'Unknown';
                        const pAge = patientAgeDisplay(p); // OR-127: DOB wins over stored age
                        const sub = [p.mobile || mobile, pAge ? `Age ${pAge}` : '', p.gender || '']
                          .filter(Boolean).join(' · ');
                        return (
                          <button
                            key={p.patient_id || p.id}
                            type="button"
                            onClick={() => setPatient(p)}
                            className="w-full flex items-center justify-between gap-2 text-left px-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 mb-1.5 cursor-pointer hover:border-teal-500 hover:bg-teal-50 dark:hover:bg-slate-700"
                          >
                            <span>
                              <span className="block font-medium text-slate-800 dark:text-slate-100">{pName}</span>
                              <span className="block text-[12px] text-slate-500 dark:text-slate-400">{sub}</span>
                              <AdmittedElsewhereLabel patient={p} />
                              <span className="block mt-1"><AbhaBadge status={p.abha_status} isDark={document.documentElement.classList.contains('dark')} /></span>
                            </span>
                            <span className="shrink-0 px-3 py-1 rounded-full bg-teal-600 text-white text-[11px] font-semibold">Select</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* 2. ADMISSION DETAILS */}
          <div className={`${cardFor(needDiagnosis || needConsent)} ${!patient ? 'opacity-60 pointer-events-none' : ''}`} data-testid="card-admission-details">
            <div className={cardH}>
              <span className={stepChip(2, needDiagnosis)}>2</span>
              <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Admission details</h2>
            </div>
            <div className={cardB}>
              <div className="mb-3">
                <label className={labelCls}>Admission source</label>
                <div className="inline-flex flex-wrap gap-1.5 p-1 rounded-lg bg-slate-100 dark:bg-slate-900" role="group" aria-label="Admission source">
                  {ADMISSION_SOURCES.map((s) => (
                    <button
                      key={s.value}
                      type="button"
                      onClick={() => onSourceChange(s.value)}
                      className={`px-3 py-1.5 rounded-md text-[12.5px] font-medium border ${
                        source === s.value
                          ? 'bg-teal-600 border-teal-600 text-white'
                          : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100'
                      }`}
                    >
                      {s.value === ADMISSION_SOURCE.EMERGENCY ? '🚨 ' : ''}{s.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div>
                  <label className={labelCls}>Primary doctor{elective ? ' *' : ' (optional)'}</label>
                  <select
                    className={inputCls}
                    value={primaryDoctorId}
                    onChange={(e) => onDoctorChange(e.target.value)}
                    aria-label="Primary doctor"
                  >
                    <option value="">— Select doctor —</option>
                    {doctorOptions.map((d) => (
                      <option key={d.member_id} value={d.member_id}>
                        Dr. {d.name || d.display_name}{d.specialization ? ` — ${SPECIALIZATION_LABELS[d.specialization] || d.specialization}` : ''}
                      </option>
                    ))}
                  </select>
                  {doctorsLoading && (
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1" data-testid="doctors-loading">{DOCTORS_LOADING_COPY}</p>
                  )}
                  {doctorsError && (
                    <p className="text-[11px] text-red-700 dark:text-red-300 mt-1" data-testid="doctors-error">{DOCTORS_LOAD_ERROR_COPY}</p>
                  )}
                  {!doctorsLoading && !doctorsError && doctors.length === 0 && (
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1" data-testid="doctors-empty">{doctorsEmptyCopy(pickerIsWalled)}</p>
                  )}
                  {pickerIsWalled && (
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1" data-testid="doctors-scoped-footer">{DOCTORS_SCOPED_FOOTER_COPY}</p>
                  )}
                </div>
                <div>
                  <label className={labelCls}>Department{resolution.locked ? '' : ''}</label>
                  <select
                    className={inputCls}
                    value={departmentId}
                    onChange={(e) => onDeptChange(e.target.value)}
                    disabled={resolution.locked}
                    aria-label="Department"
                  >
                    <option value="">— No department —</option>
                    {resolution.options.map((o) => (
                      <option key={o.dept_id} value={o.dept_id}>{o.name}</option>
                    ))}
                  </select>
                  {resolution.locked && (
                    <p className="text-[11px] text-teal-700 dark:text-teal-300 mt-1">Set automatically from the primary doctor.</p>
                  )}
                </div>
              </div>

              <div className="mb-3">
                <label className={labelCls}>Provisional diagnosis{diagnosisRequired ? ' *' : ' (optional)'}</label>
                <textarea
                  className={fieldFor(needDiagnosis)}
                  data-testid="diagnosis-input"
                  aria-invalid={(needDiagnosis && showFieldErrors) || undefined}
                  rows={2}
                  value={diagnosis}
                  onChange={(e) => setDiagnosis(e.target.value)}
                  placeholder="e.g. Unstable angina — planned angiography"
                  aria-label="Provisional diagnosis"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div>
                  <label className={labelCls}>Payer type</label>
                  <select className={inputCls} value={payerType} onChange={(e) => setPayerType(e.target.value)} aria-label="Payer type">
                    {PAYER_TYPES.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Admission date &amp; time (optional)</label>
                  <input
                    type="datetime-local"
                    className={inputCls}
                    value={admittedAt}
                    min={admittedAtBounds.current.min}
                    max={admittedAtBounds.current.max}
                    onChange={(e) => setAdmittedAt(e.target.value)}
                    aria-label="Admission date & time"
                    data-testid="admitted-at"
                  />
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                    Leave blank to admit as of now. Can be backdated up to 24 hours.
                  </p>
                </div>
              </div>

              <div className="mb-3">
                <label className={labelCls}>Attender / next-of-kin (optional)</label>
                <AttenderFields value={attender} onChange={setAttender} />
              </div>

              {emergency && (
                <EmergencyMlcSection value={mlc} onChange={setMlc} />
              )}

              {/* CONSENT-CAPTURE P1 (D13) — end of Step 2, identical across
                  every ADMISSION_SOURCE / with-or-without primary doctor /
                  assign_bed_later. No patient yet → disabled + informational,
                  not hidden (dims with the rest of the card via opacity-60
                  pointer-events-none above). */}
              <div
                className=""  /* PO: consent is a required CHOICE, never a validation failure — never red */
                data-testid="consent-wrap"
              >
              <ConsentBlock
                catalogEntry={consentCatalogEntry}
                checked={consentGranted}
                onChange={setConsentGranted}
                existingEntry={digitalRecordsEntry}
                patientSelected={!!patient}
                disabledMessage="Select a patient to record consent."
              />
              </div>
            </div>
          </div>

          {/* 2b. PLANNED INTERVENTIONS — elective only */}
          {elective && patient && (
            <div className={cardCls} data-testid="planned-card">
              <div className={cardH}>
                <span className="w-5 h-5 rounded-full bg-teal-600 text-white text-[11px] font-semibold flex items-center justify-center">2b</span>
                <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Planned interventions</h2>
                <span className="text-[11px] px-2 py-0.5 rounded-full border border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400">optional</span>
                <span className="text-[11px] px-2 py-0.5 rounded-full border border-teal-400 bg-teal-100 dark:bg-teal-900/40 text-teal-800 dark:text-teal-200">Surgeries &amp; procedures</span>
              </div>
              <div className={cardB}>
                <PlannedInterventionsPicker
                  anchorDeptId={departmentId || null}
                  onAdd={(line) => setPlannedInterventions((list) => [...list, line])}
                />
                {plannedInterventions.length === 0 && (
                  <p className="mt-3 text-[11.5px] text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900 border border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-2.5">
                    Nothing planned yet — that&rsquo;s fine. The doctor can add procedures or lab tests later.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* 3. BED */}
          <div className={`${cardFor(needBed)} ${!patient ? 'opacity-60 pointer-events-none' : ''}`} data-testid="card-bed">
            <div className={cardH}>
              <span className={stepChip(3, needBed)}>3</span>
              <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Bed assignment</h2>
              <span className="text-[11px] px-2 py-0.5 rounded-full border border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400">
                {emergency ? 'optional' : assignLater ? 'later' : 'required'}
              </span>
            </div>
            <div className={cardB}>
              {elective && (
                <label
                  className="mb-3 flex items-center gap-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 p-2.5 cursor-pointer"
                  data-testid="assign-bed-later-toggle"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-teal-600"
                    checked={assignLater}
                    onChange={(e) => onToggleAssignLater(e.target.checked)}
                    aria-label="Assign bed later"
                  />
                  <span>
                    <span className="block text-[12.5px] font-medium text-slate-800 dark:text-slate-100">Assign a bed later</span>
                    <span className="block text-[11.5px] text-slate-500 dark:text-slate-400">Admit now and pick a bed from Manage Admission later.</span>
                  </span>
                </label>
              )}
              {emergency && (
                <div className="mb-3 rounded-lg border border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 text-[12.5px] p-2.5" data-testid="emergency-bed-note">
                  <b>Emergency admission:</b> a bed is optional. Admit now and assign a bed later from Manage Admission.
                </div>
              )}
              {bedConflict && (
                <div className="mb-3 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5" data-testid="bed-conflict">
                  <b>That bed was just taken.</b> Nothing you entered is lost — please pick another bed.
                </div>
              )}
              {assignLater && (
                <div className="mb-3 rounded-lg border border-amber-400 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 text-[12.5px] p-2.5" data-testid="assign-later-note">
                  <b>A bed will be assigned later.</b> This patient will show as <b>Awaiting bed</b> on Manage Admission until you assign one.
                </div>
              )}
              {selectedBed && (
                <div className="mb-3 rounded-lg border border-teal-500 bg-teal-50 dark:bg-teal-900/20 text-teal-800 dark:text-teal-200 text-[12.5px] p-2.5 flex items-center gap-2" data-testid="bed-pin">
                  <span>✓ Selected: <b>{selectedBed.label}</b>{selectedBed.bedCategory ? ` · ${selectedBed.bedCategory.name}` : ''}</span>
                  <button type="button" className="ml-auto px-2 py-1 text-[11px] rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800" onClick={() => setSelectedBed(null)}>Clear</button>
                </div>
              )}
              <div
                className={`${assignLater ? 'opacity-50 pointer-events-none' : ''} ${needBed && showFieldErrors ? 'rounded-lg border-2 border-red-400 dark:border-red-600 bg-red-50 dark:bg-red-900/20 p-2' : ''}`}
                aria-disabled={assignLater}
                data-testid="bed-picker-wrap"
              >
                <WardBedPicker
                  anchorDeptId={departmentId || null}
                  value={selectedBed ? selectedBed.bed_id : null}
                  onChange={onSelectBed}
                  disabled={assignLater}
                />
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT RAIL: estimate + submit */}
        <div className="lg:sticky lg:top-4">
          <div className={cardCls}>
            <div className={cardB}>
              <EstimatePanel
                value={estimateValue}
                onChangeDays={setExpectedStayDays}
                onChangeOtherLines={setOtherLines}
                onRemoveIntervention={removeIntervention}
                assignLater={elective && assignLater && !selectedBed}
                expectedBedCategoryId={expectedBedCategoryId}
                onChangeExpectedBedCategory={setExpectedBedCategoryId}
              />
            </div>
          </div>

          <div className={cardCls}>
            <div className={cardB}>
              {duplicate && (
                <div className="mb-3 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5" data-testid="duplicate-block">
                  <div className="font-semibold mb-1">
                    {ADMISSION_ACCESS_ERROR_COPY[ADMISSION_ACCESS_ERROR_CODE.DUPLICATE_ACTIVE_ADMISSION]}
                  </div>
                  {duplicate.in_scope === false ? (
                    <div data-testid="duplicate-out-of-scope">
                      {duplicateOutOfScopeLines(duplicate).map((line) => <p key={line}>{line}</p>)}
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="underline"
                      onClick={() => onViewAdmission && onViewAdmission(duplicate.admission_id)}
                    >
                      View existing admission {duplicate.ip_number ? `(${duplicate.ip_number})` : ''}
                    </button>
                  )}
                </div>
              )}
              {error && (
                <div className="mb-3 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5" data-testid="form-error">
                  {error}
                </div>
              )}
              {unmetItems.length > 0 && (
                <div
                  className={`mb-3 rounded-lg border text-[12.5px] p-2.5 ${
                    showChecklistError
                      ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200'
                      : 'border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
                  }`}
                  data-testid="readiness-checklist"
                >
                  {/* PO: guidance before an attempt, error after. Red only keeps
                      its meaning ("you tried and this stopped you") if it is
                      absent before you try — red on arrival devalues red
                      everywhere else on the form. Neutral slate, not amber:
                      amber reads "warning", also wrong pre-attempt. */}
                  <div className="font-semibold mb-1">
                    {showChecklistError ? 'To admit, you still need to:' : "To admit, you'll need:"}
                  </div>
                  <ul className="list-none space-y-0.5">
                    {unmetItems.map((item) => (
                      <li key={item}>• {item}</li>
                    ))}
                  </ul>
                </div>
              )}
              <button
                type="button"
                onClick={submit}
                disabled={!canSubmit}
                className="w-full px-4 py-2.5 rounded-lg bg-teal-600 text-white text-sm font-semibold disabled:opacity-60"
                data-testid="submit-admit"
              >
                {submitting ? 'Admitting…' : emergency && !selectedBed ? 'Admit now (no bed)' : 'Admit patient'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {confirmClearBed && (
      <ConfirmationModal
        isOpen
        icon="🛏️"
        title="Assign a bed later?"
        message="The selected bed will be cleared."
        ctaLabel="Assign later"
        cancelLabel="Keep bed"
        onConfirm={onConfirmClearBed}
        onCancel={() => setConfirmClearBed(false)}
      />
      )}

      {showRegisterDialog && (
        <InlineRegisterPatientDialog
          mobile={mobile.trim()}
          consentCatalogEntry={consentCatalogEntry}
          initialAbhaSnapshot={registerAbhaSnapshot}
          onCancel={() => { setShowRegisterDialog(false); setRegisterAbhaSnapshot(null); setScanClaim(null); }}
          onAbhaRemoved={() => setScanClaim(null)}   // Remove = "don't use this scan": register without claiming it
          onRegistered={(newPatient, note) => {
            if (scanClaim && canScanShare) { const id = scanClaim.scanId; setScanClaim(null); runClaim(newPatient, id); }
            else setPatient(newPatient);
            setAbhaNote(note || '');
            setShowRegisterDialog(false);
            setRegisterAbhaSnapshot(null);
            setSearchResults(null);
          }}
        />
      )}

      {(abhaVerifying || scanExisting) && patient && (
        <AbhaVerifyModal
          patient={patient}
          mobile={patient.mobile || mobile}
          initialMode={scanExisting ? 'verify' : abhaStatusMeta(patient.abha_status).ctaIntent}
          notice={scanExisting ? EXISTING_PATIENT_VERIFY_NOTICE : ''}
          onComplete={(snapshot) => { if (!snapshot) return; setAbhaSnapshot(snapshot); setAbhaVerifying(false); setScanClaim(null); }}
          onClose={() => { setAbhaVerifying(false); setScanClaim(null); }}
        />
      )}
      {abhaSnapshot && patient && (
        <AbhaLinkConfirmDialog
          patientId={patient.patient_id || patient.id}
          snapshot={abhaSnapshot}
          onLinked={() => { setPatient((p) => (p ? { ...p, abha_status: 'VERIFIED' } : p)); setAbhaSnapshot(null); }}
          onRegisterNew={() => {
            setRegisterAbhaSnapshot(abhaSnapshot);
            setAbhaSnapshot(null);
            setPatient(null);
            setShowRegisterDialog(true);
          }}
          onCancel={() => setAbhaSnapshot(null)}
          onVerifyAgain={() => { setAbhaSnapshot(null); setAbhaVerifying(true); }}
        />
      )}
    </div>
  );
}
